import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { IonicModule } from "@ionic/angular";
import { ROUTING_TRAMITE_PENSION } from "@constants/url.constant";
import { PREGUNTAS_ANTECEDENTES_PENSION } from "@constants/lista-preguntas.constants";
import { StorageService } from "@services/storage/storage.service";
import { Util } from "@shared/common/utils/util";
import { ACCOUNTS } from "../personal-data/personal-data.const";
import { TramiteStepperService } from "../../services/tramite-stepper.service";
import { PreguntasComponent } from "@shared/components/preguntas/preguntas.component";
import { BotonVolverComponent } from "../../components/boton-volver/boton-volver.component";
import { GtmService } from "@services/gtm/gtm.service";

@Component({
  selector: "app-tramite-pension-antecedentes",
  templateUrl: "./tramite-pension-antecedentes.component.html",
  styleUrls: ["./tramite-pension-antecedentes.component.scss"],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, PreguntasComponent, BotonVolverComponent]
})
export class TramitePensionAntecedentesComponent implements OnInit, OnDestroy {

  form!: FormGroup;
  questions = PREGUNTAS_ANTECEDENTES_PENSION;
  private readonly ACCOUNT_IDS_VOLUNTARIO = ['CAV', 'CCV', 'CVC'];
  private readonly STORAGE_KEY = 'antecedentesPension';
  private readonly STORAGE_KEY_VISIBLE_COUNT = 'antecedentesPension_visibleCount';
  balancesVoluntario: any;
  isFormReady = false;
  visibleQuestionsCount = 1;
  @ViewChildren(PreguntasComponent, { read: ElementRef })
  questionElements!: QueryList<ElementRef>;
  activeQuestionIndex = 0;
  resumenRefreshToken = 0;
  private requestLayoutHost: HTMLElement | null = null;

  constructor(private readonly fb: FormBuilder, private readonly router: Router, private readonly activatedRoute: ActivatedRoute,
    private readonly util: Util, private readonly storageService: StorageService,
    private readonly tramiteStepper: TramiteStepperService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly gtmService: GtmService
  ) { }

  ngOnDestroy(): void {
    this.restoreRequestLayoutHost();
  }

  ionViewWillLeave(): void {
    this.restoreRequestLayoutHost();
  }

  ionViewWillEnter(): void {
    this.applyRequestSingleScrollLayout();
  }

  async ngOnInit() {
    // Mismo patrón que paso 2 y paso 6: un solo scroll en el layout del trámite.
    this.applyRequestSingleScrollLayout();
    this.tramiteStepper.setStepCurrent(3);

    await this.filtrarPreguntasSegunProductos();

    const controls: any = {};

    this.questions.forEach((q: any) => {

      // radio yes/no
      controls[q.controlName] = [null, Validators.required];

      // preguntas con monto editable: monto total
      if (q.amountControl) {
        controls[q.amountControl] = [q.amount];
        controls[q.currencyControl] = ['CLP'];

        // Crear controles para cada categoría de esta pregunta
        if (q.accountBalance?.fund) {
          q.accountBalance.fund.forEach((fondo: any) => {
            if (fondo.subBalances && Array.isArray(fondo.subBalances)) {
              fondo.subBalances.forEach((subBalance: any) => {
                if (subBalance.category) {
                  // Generar nombre único del control: transferFundsCAV_C_RETIRO10
                  const controlName = `${q.controlName}_${fondo.id}_${subBalance.category}`.toLowerCase();
                  // Valor inicial es el clpBalance del subBalance
                  controls[controlName] = [{ value: subBalance.clpBalance, disabled: false }];
                }
              });
            }
          });
        }
      }

    });

    controls.confirm = [{ value: false, disabled: true }];

    this.form = this.fb.group(controls);
    this.isFormReady = true;

    // Cargar respuestas guardadas
    this.loadSavedAnswers();

    this.form.valueChanges.subscribe(() => {
      this.updateProgressiveQuestions();
      this.checkAllAnswered();
      this.saveAnswersAutomatically();
    });
  }

  private scrollToLastQuestion() {
    setTimeout(() => {
      const elements = this.questionElements?.toArray();
      if (!elements?.length) {
        return;
      }

      const lastElement = elements[elements.length - 1].nativeElement as HTMLElement;
      const scrollContainer = this.getRequestScrollContainer();

      // Evita que scrollIntoView mueva el scroll del shell (doble scroll).
      if (scrollContainer) {
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = lastElement.getBoundingClientRect().top;
        const nextTop = scrollContainer.scrollTop + (elementTop - containerTop) - 16;
        scrollContainer.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
        return;
      }

      lastElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  }

  private getRequestScrollContainer(): HTMLElement | null {
    const requestHost = this.elementRef.nativeElement.closest(
      "app-tramite-pension-request"
    );
    return (requestHost?.querySelector(".container") as HTMLElement | null) ?? null;
  }
  private async filtrarPreguntasSegunProductos() {
    await this.fillBalanceTableVoluntario();

    if (this.balancesVoluntario.length > 0) {
      // Crear mapa de balances por ID para búsqueda rápida
      const balancesMap = new Map(
        this.balancesVoluntario.map((balance: any) => [balance.id, balance])
      );

      // Filtrar preguntas: MANTENER solo las que corresponden a productos que el cliente TIENE
      this.questions = this.questions
        .filter(q => q.type ? balancesMap.has(q.type) : true)  // Mantener si tiene el tipo o si no tiene type
        .map(q => {
          // Si la pregunta corresponde a un producto que el cliente tiene, enriquecer con balance
          if (q.type && balancesMap.has(q.type)) {
            const balance = balancesMap.get(q.type) as any;
            return {
              ...q,
              accountBalance: balance,
              amount: balance.amount  // Usar el balance total como valor predeterminado
            };
          }
          return q;
        });        
    } else {
      // Si no tiene cuentas voluntarias, eliminar las preguntas de transferencia
      this.questions = this.questions.filter(q => 
        !['transferFundsCAV', 'transferFundsCCV', 'transferFundsAPVC'].includes(q.controlName)
      );
    }
  }

  private updateProgressiveQuestions() {

    let advanced = false;

    while (
      this.visibleQuestionsCount < this.questions.length &&
      this.form.get(
        this.questions[this.visibleQuestionsCount - 1].controlName
      )?.valid
    ) {
      const currentQuestion = this.questions[this.visibleQuestionsCount - 1];

      // Si la pregunta actual es de monto editable, no avanzar automáticamente.
      // Debe avanzar solo por Guardar o por selección explícita de No.
      if (currentQuestion.amountControl) {
        break;
      }

      // Si la siguiente pregunta tiene amountControl, solo avanza una vez y se detiene
      const nextQuestion = this.questions[this.visibleQuestionsCount];
      this.visibleQuestionsCount++;
      this.activeQuestionIndex = this.visibleQuestionsCount - 1;
      advanced = true;
      if (nextQuestion?.amountControl) {
        break;
      }
    }

    if (advanced) {
      this.scrollToLastQuestion();
    }
  }

  private checkAllAnswered() {
    const allAnswered = this.questions.every(q =>
      this.form.get(q.controlName)?.valid
    );

    const confirm = this.form.get('confirm');
    const confirmWasEnabled = confirm?.enabled ?? false;

    if (allAnswered) {
      if (!confirmWasEnabled) {
        this.scrollToLastQuestion();
      }
      confirm?.enable({ emitEvent: false });
    } else {

      confirm?.disable({ emitEvent: false });
      confirm?.setValue(false, { emitEvent: false });
    }
  }

  canContinue(): boolean {
    return this.isFormReady && this.form.valid && this.form.get('confirm')?.value;
  }

  private loadSavedAnswers(): void {
    const savedData = sessionStorage.getItem(this.STORAGE_KEY);
    const savedVisibleCount = sessionStorage.getItem(this.STORAGE_KEY_VISIBLE_COUNT);

    if (savedData) {
      const data = JSON.parse(savedData);
      this.form.patchValue(data, { emitEvent: false });
    }

    if (savedVisibleCount) {
      this.visibleQuestionsCount = Number.parseInt(savedVisibleCount, 10);
      this.activeQuestionIndex = this.visibleQuestionsCount - 1;
    }

    // Actualizar preguntas visibles después de cargar datos
    setTimeout(() => {
      this.updateProgressiveQuestions();
      this.checkAllAnswered();
    }, 0);
  }

  private saveAnswersAutomatically(): void {
    const data = this.form.getRawValue();
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    sessionStorage.setItem(this.STORAGE_KEY_VISIBLE_COUNT, this.visibleQuestionsCount.toString());
  }

  onSaveAmount(): void {
    // Avanzar a la siguiente pregunta cuando se guarda el monto
    this.resumenRefreshToken++;
    if (this.visibleQuestionsCount < this.questions.length) {
      this.visibleQuestionsCount++;
      this.activeQuestionIndex = this.visibleQuestionsCount - 1;
      this.scrollToLastQuestion();
      this.updateProgressiveQuestions();
      this.checkAllAnswered();
      this.saveAnswersAutomatically();
    }
  }

  submit() {
    if (!this.canContinue()) return;

    this.gtmService.trackClick('button', 'Continuar', 'boton_principal', '/pensions/tramite-pensiones/solicitud/via-pago');
    const data = this.form.getRawValue();

    // Guardar las respuestas finales confirmadas
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));

    this.router.navigate([`../${ROUTING_TRAMITE_PENSION.VIA_PAGO}`], {
      relativeTo: this.activatedRoute,
    });
  }

  goBack() {
    this.router.navigate([`../${ROUTING_TRAMITE_PENSION.DATOS_PERSONALES}`], {
      relativeTo: this.activatedRoute,
    });
  }

  private processBalances(cuentas: any[], allowedAccountIds: string[]) {
    if (!cuentas) return [];

    return cuentas
      .filter(balance =>
        allowedAccountIds.includes(balance.accountConfiguration?.id)
        && Number(balance.amount) > 0
      )
      .map(balance => ({
        id: balance.accountConfiguration.id,
        accountType: this.getAccountTitle(balance.accountConfiguration.id),
        fund: balance.funds,
        amount: balance.amount,
        balancePesos: this.util.currencyFormat(balance.amount),
        balanceInstallments: balance.funds.map((fund: any) => fund.shareBalance),
      }));
  }
  getAccountTitle(id: string) {
    return ACCOUNTS.find((acc) => acc.value === id)?.title;
  }

  async fillBalanceTableVoluntario() {
    const cuentas = await this.storageService.get("accounts");
    this.balancesVoluntario = this.processBalances(cuentas, this.ACCOUNT_IDS_VOLUNTARIO);    
  }

  onCheckboxKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      const currentValue = this.form.get('confirm')?.value;
      this.form.patchValue({ confirm: !currentValue });
    }
  }

  openQuestion(index: number): void {
    this.activeQuestionIndex = index;
  }

  closeQuestion(): void {
    this.activeQuestionIndex = -1;
  }

  /**
   * Mismo patrón que personal-data (paso 2) y confirm-data (paso 6):
   * el host no desborda al shell; solo hace scroll `.container` del request.
   */
  private applyRequestSingleScrollLayout(): void {
    this.requestLayoutHost = this.elementRef.nativeElement.closest(
      "app-tramite-pension-request"
    );
    if (!this.requestLayoutHost) {
      return;
    }
    this.requestLayoutHost.style.setProperty("display", "block");
    this.requestLayoutHost.style.setProperty("height", "100%");
    this.requestLayoutHost.style.setProperty("max-height", "100%");
    this.requestLayoutHost.style.setProperty("min-height", "0");
    this.requestLayoutHost.style.setProperty("overflow", "hidden");
  }

  private restoreRequestLayoutHost(): void {
    if (!this.requestLayoutHost) {
      return;
    }
    this.requestLayoutHost.style.removeProperty("display");
    this.requestLayoutHost.style.removeProperty("height");
    this.requestLayoutHost.style.removeProperty("max-height");
    this.requestLayoutHost.style.removeProperty("min-height");
    this.requestLayoutHost.style.removeProperty("overflow");
    this.requestLayoutHost = null;
  }
}